/**
 * Die Passive des Heroes als runder Knopf (Rueckmeldung vom 26.09.2026).
 *
 * Sie sitzt neben der Faehigkeitengruppe und nicht mehr im Heldenstatus: Sie
 * gehoert zum Kampf, nicht zu Leben und Ausruestung. Bewusst KLEINER als eine
 * Faehigkeit - wirken laesst sie sich nicht, sie laeuft von selbst.
 *
 * Der Ring zeigt, wie voll die Passive gerade ist - gemessen an IHREM eigenen
 * Maximum. Bei Stand Your Ground sind 20 Prozent Schadensreduktion das Beste,
 * was sie kann; dann ist der Ring voll. Ein Ring, der bei 20 Prozent nur zu
 * einem Fuenftel gefuellt waere, wuerde etwas anderes behaupten.
 *
 * Die Zahl darunter sagt, was sie GERADE bringt. Was sie ueberhaupt tut, steht
 * im Tooltip.
 */

import { MARKED_PREY, MOMENTUM, STAND_YOUR_GROUND } from '@dueline/game-data';
import { getMaxMarkedStacks } from '@dueline/game-rules';
import { t } from '../i18n/index.js';
import { passivBild } from './faehigkeitBilder.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';

interface PassiveInfo {
  name: string;
  beschreibung: string;
  farbe: 'overflow' | 'mark' | 'momentum' | 'guard';
  /** Fuellstand 0 bis 1, gemessen am eigenen Maximum dieser Passive. */
  anteil: number;
  /** Was sie gerade bringt. */
  wert: string;
  /** Ist sie voll ausgereizt? */
  bereit: boolean;
}

/** Name, Stand und Erklaerung der Passive dieses Heroes. */
export function describePassive(snapshot: MatchSnapshot): PassiveInfo | null {
  if (snapshot.heroId === 'arcanist') {
    const anteil = Math.max(0, Math.min(1, snapshot.overflowCharge));
    const voll = anteil >= 1;
    return {
      name: t.overflow.label,
      beschreibung: t.overflow.hint,
      farbe: 'overflow',
      anteil,
      wert: voll ? t.overflow.ready : `${Math.floor(anteil * 100)} %`,
      bereit: voll,
    };
  }

  if (snapshot.heroId === 'deadeye') {
    const hoechste = getMaxMarkedStacks(snapshot.talentId);
    const stufen = Math.max(0, Math.min(hoechste, snapshot.markStacks));
    const zuschlag = Math.round(Math.max(0, stufen - 1) * MARKED_PREY.damagePerStack * 100);
    return {
      name: t.markedPrey.label,
      beschreibung: t.markedPrey.hint,
      farbe: 'mark',
      anteil: stufen / hoechste,
      wert: `+${zuschlag} %`,
      bereit: stufen >= hoechste,
    };
  }

  if (snapshot.heroId === 'shade') {
    const stufen = Math.max(0, Math.min(MOMENTUM.maxStacks, snapshot.momentumStacks));
    const tempo = Math.round(stufen * MOMENTUM.moveSpeedPerStack * 100);
    return {
      name: t.momentum.label,
      beschreibung: t.momentum.hint,
      farbe: 'momentum',
      anteil: stufen / MOMENTUM.maxStacks,
      wert: `+${tempo} %`,
      bereit: stufen >= MOMENTUM.maxStacks,
    };
  }

  if (snapshot.heroId === 'vanguard') {
    /*
     * Die Passive allein steht nicht im Zustand - dort steht die GESAMTE
     * Schadensreduktion. Das ist der ehrlichste Wert fuer die Anzeige: Er sagt,
     * wie viel gerade abgehalten wird. Gemessen wird er am Maximum der Passive;
     * wirken Skills mit, ist der Ring eben voll.
     */
    const anteil = Math.max(
      0,
      Math.min(1, snapshot.damageReduction / STAND_YOUR_GROUND.maximumDamageReduction),
    );
    return {
      name: t.standYourGround.label,
      beschreibung: t.standYourGround.hint,
      farbe: 'guard',
      anteil,
      wert: `${Math.round(snapshot.damageReduction * 100)} %`,
      bereit: anteil >= 1,
    };
  }

  return null;
}

export function PassiveOrb({ snapshot }: { snapshot: MatchSnapshot }) {
  const passive = describePassive(snapshot);
  if (!passive || !snapshot.heroAlive) return null;

  const anteil = Math.max(0, Math.min(1, passive.anteil));
  const bild = passivBild(snapshot.heroId);

  return (
    <button
      type="button"
      className={
        'dl-passive' +
        (passive.bereit ? ' dl-passive--ready' : '') +
        (bild ? ' dl-passive--bild' : '')
      }
      title={`${passive.name} - ${passive.beschreibung}`}
      aria-label={`${t.passive.label}: ${passive.name}. ${passive.beschreibung}`}
    >
      {/* Das Symbol der Passive (16.10.2026) liegt unter Ring und Zahl. */}
      {bild && (
        <picture className="dl-passive__bild" aria-hidden="true">
          <source type="image/avif" srcSet={bild.avif} sizes="64px" />
          <source type="image/webp" srcSet={bild.webp} sizes="64px" />
          <img src={bild.ersatz} alt="" decoding="async" />
        </picture>
      )}
      <span
        className={`dl-passive__ring dl-passive__ring--${passive.farbe}`}
        style={{ ['--dl-passive-anteil' as string]: `${anteil * 360}deg` }}
        aria-hidden="true"
      />
      <span className="dl-passive__value">{passive.wert}</span>
    </button>
  );
}
